import React from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaBolt, FaCheckCircle, FaUsers, FaSearch, FaLightbulb, FaPalette, FaRocket } from "react-icons/fa";
import SEO from "../../components/common/SEO";
import { SectionHeader, Reveal, Breadcrumb } from "../../components/common";

const NAME_DECODER = [
  { letter: "A", word: "Art", desc: "The craft in every pixel, curve and color choice." },
  { letter: "V", word: "Vision", desc: "Seeing what your brand could become before it exists." },
  { letter: "X", word: "Experience", desc: "Design that people feel, not just look at." },
];

const VALUES = [
  { icon: FaBolt, title: "Fast Delivery", desc: "Quick turnaround times without compromising quality." },
  { icon: FaCheckCircle, title: "Quality Assured", desc: "Rigorous quality checks at every stage." },
  { icon: FaUsers, title: "Team Collaboration", desc: "Working closely with your team throughout." },
];

const PROCESS = [
  { icon: FaSearch, title: "Discovery & Research", desc: "Deep dive into your brand, audience, and objectives through comprehensive research." },
  { icon: FaLightbulb, title: "Strategy & Planning", desc: "Strategic roadmap with clear objectives and success metrics tailored to your needs." },
  { icon: FaPalette, title: "Design & Development", desc: "Bringing concepts to life through iterative processes and modern technologies." },
  { icon: FaRocket, title: "Launch & Support", desc: "Smooth deployment with ongoing support and optimization for peak performance." },
];

const STATS = [
  ["120+", "Projects shipped", "From first sketch to final launch"],
  ["85+", "Happy clients", "Partnerships built on trust"],
  ["5+", "Years of craft", "Proven creative problem solving"],
];

const About = () => {
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "About Arvix Creative - Creative Studio",
        "description": "Learn about Arvix Creative, a passionate team with years of experience creating compelling visual solutions and websites for brands and businesses. Based in Chattogram, Bangladesh.",
        "url": "https://arvixcreative.com/about",
        "mainEntity": {
            "@type": "Organization",
            "name": "Arvix Creative",
            "alternateName": ["Tanvirus Pixels", "Tanvir Creative Studio"],
            "url": "https://arvixcreative.com",
            "logo": "https://arvixcreative.com/logo512.png",
            "description": "Professional creative studio specializing in brand identity, UI/UX design, web development, and visual solutions.",
            "address": {
                "@type": "PostalAddress",
                "addressLocality": "Chattogram",
                "addressCountry": "Bangladesh"
            },
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+8801645076455",
                "contactType": "customer service",
                "email": "arvixcreative@gmail.com"
            },
            "sameAs": [
                "https://www.facebook.com/arvix.creative",
                "https://www.instagram.com/arvix.creative"
            ]
        }
    };

    return (
        <>
            <SEO
                title="About Arvix Creative - Creative Studio | Tanvirus Pixels"
                description="Learn about Arvix Creative, a passionate team with years of experience creating compelling visual solutions and websites for brands and businesses. Based in Chattogram, Bangladesh."
                keywords="about arvix, about arvix creative, creative studio, brand identity, UI/UX design, web development, Bangladesh, Chattogram, tanvirus, tanvir, pixels, tanvirus pixels"
                url="https://arvixcreative.com/about"
                structuredData={structuredData}
            />
            <div className="min-h-screen pt-24 pb-20">
                <Breadcrumb items={[{ label: "About" }]} className="mb-8" />
                {/* Page hero */}
                <div className="grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-end">
                    <div>
                        <Reveal>
                            <div className="flex items-center gap-3">
                                <span className="tick" aria-hidden="true" />
                                <p className="text-xs font-bold tracking-[0.25em] uppercase text-designer-primary">
                                    About us
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={90}>
                            <h1 className="text-balance mt-5 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-designer-dark">
                                A small studio with a big wall of finished work
                            </h1>
                        </Reveal>
                    </div>
                    <Reveal delay={170}>
                        <p className="text-base md:text-lg text-gray-600 leading-relaxed lg:justify-self-end lg:max-w-md">
                            Since 2018 we have turned bold ideas into memorable visual stories for
                            brands across Bangladesh and beyond. This page is the story behind the work.
                        </p>
                    </Reveal>
                </div>

                {/* Story + name decoder */}
                <div className="mt-12 md:mt-16 grid gap-5 md:gap-6 lg:grid-cols-2">
                    <Reveal className="h-full">
                        <div className="h-full rounded-[2rem] border border-designer-dark/10 bg-white p-7 md:p-10">
                            <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-gray-400">
                                Our story
                            </p>
                            <h2 className="mt-3 font-display text-2xl md:text-3xl font-bold text-designer-dark leading-tight">
                                Hello, we are <span className="text-designer-primary">Arvix Creative</span>
                            </h2>
                            <div className="mt-5 space-y-4 text-gray-600 leading-relaxed">
                                <p>
                                    We are a passionate team with years of experience creating compelling
                                    visual solutions and websites for brands and businesses. Our philosophy
                                    centers on the perfect balance of aesthetics and functionality.
                                </p>
                                <p>
                                    We have worked with clients across many industries, helping them
                                    communicate their message through thoughtful creative solutions. We
                                    believe great work transforms businesses and builds meaningful
                                    connections with audiences.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={120} className="h-full">
                        <div className="relative h-full overflow-hidden rounded-[2rem] bg-designer-dark p-7 md:p-10">
                            <div className="absolute inset-0 texture-grid-dark opacity-60" aria-hidden="true" />
                            <div className="relative">
                                <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-white/40">
                                    What does ArViX mean?
                                </p>
                                <div className="mt-5 space-y-3">
                                    {NAME_DECODER.map((item) => (
                                        <div
                                            key={item.letter}
                                            className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4"
                                        >
                                            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-designer-primary font-display text-2xl font-extrabold text-white">
                                                {item.letter}
                                            </span>
                                            <span>
                                                <span className="block font-display text-lg font-bold text-white leading-tight">
                                                    {item.word}
                                                </span>
                                                <span className="block text-sm text-white/55">{item.desc}</span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-5 text-sm leading-relaxed text-white/55">
                                    Art Vision Experience: our mission to transform ideas into visual
                                    narratives that resonate and create lasting impact.
                                </p>
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Stats band */}
                <div className="bleed mt-12 md:mt-16 bg-[#FBFAF9] border-y border-designer-dark/10">
                    <dl className="py-10 md:py-14 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-0 sm:divide-x sm:divide-designer-dark/10">
                        {STATS.map(([value, label, note], i) => (
                            <Reveal key={label} delay={i * 100} className="sm:px-8 sm:first:pl-0 sm:last:pr-0">
                                <dd className="font-display font-extrabold text-5xl md:text-6xl text-designer-dark leading-none tabular-nums">
                                    {value}
                                </dd>
                                <dt className="mt-3 text-sm font-bold text-designer-dark">{label}</dt>
                                <dd className="mt-1 text-[13px] text-gray-500">{note}</dd>
                            </Reveal>
                        ))}
                    </dl>
                </div>

                {/* Values */}
                <div className="mt-16 md:mt-24">
                    <SectionHeader
                        index="01"
                        eyebrow="Why us"
                        title={<>The three promises behind every project.</>}
                        lede="Simple standards we hold ourselves to, whether the job is a logo or a full launch."
                        layout="split"
                    />
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                        {VALUES.map((value, i) => (
                            <Reveal key={value.title} delay={i * 100} className="h-full">
                                <div className="h-full rounded-3xl border border-designer-dark/10 bg-white p-7 transition-shadow duration-300 hover:shadow-[0_24px_60px_-28px_rgba(29,32,48,0.35)]">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-designer-primary">
                                        <value.icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-5 font-display text-xl font-bold text-designer-dark">
                                        {value.title}
                                    </h3>
                                    <p className="mt-2 text-[15px] text-gray-600 leading-relaxed">{value.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>

                {/* Process */}
                <div className="mt-16 md:mt-24">
                    <SectionHeader
                        index="02"
                        eyebrow="How we work"
                        title={<>From first call to final files in four moves.</>}
                        lede="No black boxes. You always know what happens next, and you approve every step before we take it."
                        layout="split"
                    />
                    <ol className="mt-10 border-t border-designer-dark/10">
                        {PROCESS.map((step, i) => (
                            <Reveal key={step.title} delay={Math.min(i, 3) * 80}>
                                <li className="grid grid-cols-[auto_1fr] sm:grid-cols-[64px_1fr_1.2fr] items-center gap-4 sm:gap-8 border-b border-designer-dark/10 py-6 md:py-7">
                                    <span className="numeral-outline font-display font-extrabold text-3xl sm:text-4xl leading-none">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="flex items-center gap-3 min-w-0">
                                        <step.icon className="hidden sm:block h-5 w-5 shrink-0 text-designer-primary" aria-hidden="true" />
                                        <span className="font-display font-bold text-lg sm:text-xl text-designer-dark">
                                            {step.title}
                                        </span>
                                    </span>
                                    <span className="col-span-2 sm:col-span-1 text-sm md:text-[15px] text-gray-500 leading-relaxed">
                                        {step.desc}
                                    </span>
                                </li>
                            </Reveal>
                        ))}
                    </ol>
                </div>

                {/* CTA */}
                <Reveal className="mt-16 md:mt-24">
                    <div className="relative overflow-hidden rounded-[2rem] bg-designer-dark px-7 py-12 md:p-14 text-center">
                        <div className="absolute inset-0 texture-grid-dark opacity-60" aria-hidden="true" />
                        <div
                            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-[36rem] rounded-full bg-designer-primary/20 blur-[100px] pointer-events-none"
                            aria-hidden="true"
                        />
                        <div className="relative">
                            <h2 className="text-balance mx-auto max-w-2xl font-display text-3xl md:text-4xl font-extrabold tracking-tight text-white leading-tight">
                                Like how we think? Imagine what we can make together
                            </h2>
                            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                                <Link
                                    to="/contact"
                                    className="group inline-flex items-center gap-2 rounded-full bg-designer-primary px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-red-600"
                                >
                                    Start a project
                                    <FaArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <Link
                                    to="/projects"
                                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:border-white/60 hover:bg-white/5"
                                >
                                    See the work first
                                </Link>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </>
    );
};

export default About;
